export type SketchCamera = {
  zoom: number
  panX: number
  panY: number
}

export type SketchZoomPreset = "fit" | "100" | "200"

export const FIT_CAMERA: SketchCamera = { zoom: 1, panX: 0, panY: 0 }

export const SKETCH_VIEWPORT_MIN_ZOOM = 1
export const SKETCH_VIEWPORT_MAX_ZOOM = 8
export const SKETCH_ZOOM_STEP = 1.25

const ZOOM_MATCH_EPS = 0.015

export function zoomsMatch(a: number, b: number): boolean {
  return Math.abs(a - b) <= ZOOM_MATCH_EPS * Math.max(1, a, b)
}

export function clampZoom(zoom: number): number {
  if (!Number.isFinite(zoom)) return SKETCH_VIEWPORT_MIN_ZOOM
  return Math.min(SKETCH_VIEWPORT_MAX_ZOOM, Math.max(SKETCH_VIEWPORT_MIN_ZOOM, zoom))
}

/** 1 CSS px stroke after the world camera scale. */
export function hairlinePx(zoom: number): number {
  return zoom > 0 ? 1 / zoom : 1
}

export function fitCamera(): SketchCamera {
  return { ...FIT_CAMERA }
}

export function clampPan(camera: SketchCamera, viewportW: number, viewportH: number): SketchCamera {
  const zoom = clampZoom(camera.zoom)
  if (!(viewportW > 0) || !(viewportH > 0)) {
    return { zoom, panX: 0, panY: 0 }
  }
  const minX = viewportW * (1 - zoom)
  const minY = viewportH * (1 - zoom)
  const panX = Math.min(0, Math.max(minX, camera.panX))
  const panY = Math.min(0, Math.max(minY, camera.panY))
  return { zoom, panX, panY }
}

export function zoomAt(
  camera: SketchCamera,
  anchor: { x: number; y: number },
  nextZoom: number,
  viewportW: number,
  viewportH: number,
): SketchCamera {
  const prevZoom = camera.zoom > 0 ? camera.zoom : 1
  const zoom = clampZoom(nextZoom)
  const ratio = zoom / prevZoom
  const panX = anchor.x - (anchor.x - camera.panX) * ratio
  const panY = anchor.y - (anchor.y - camera.panY) * ratio
  return clampPan({ zoom, panX, panY }, viewportW, viewportH)
}

export function centeredCamera(zoom: number, viewportW: number, viewportH: number): SketchCamera {
  const z = clampZoom(zoom)
  const panX = (viewportW - viewportW * z) / 2
  const panY = (viewportH - viewportH * z) / 2
  return clampPan({ zoom: z, panX, panY }, viewportW, viewportH)
}

/** 1 source px ≈ 1 CSS px at Fit scale of the contained image (letterbox ignored). */
export function sourceOneToOneZoom(
  natural: { width: number; height: number } | null | undefined,
  display: { width: number; height: number },
): number {
  if (!natural || !(natural.width > 0) || !(natural.height > 0)) return 1
  if (!(display.width > 0) || !(display.height > 0)) return 1
  const containScale = Math.min(display.width / natural.width, display.height / natural.height)
  if (!(containScale > 0)) return 1
  return 1 / containScale
}

export function zoomForSourcePercent(
  natural: { width: number; height: number } | null | undefined,
  display: { width: number; height: number },
  percent: number,
): number {
  const ratio = Number.isFinite(percent) ? percent / 100 : 1
  return clampZoom(sourceOneToOneZoom(natural, display) * ratio)
}

export function matchingZoomPreset(zoom: number, oneToOneZoom: number): SketchZoomPreset | null {
  if (zoomsMatch(zoom, 1)) return "fit"
  const z100 = clampZoom(oneToOneZoom)
  const z200 = clampZoom(oneToOneZoom * 2)
  if (zoomsMatch(zoom, z100)) return "100"
  if (zoomsMatch(zoom, z200)) return "200"
  return null
}

/** Percent of 1:1 source pixels when that is above Fit; otherwise percent of Fit. */
export function displayZoomPercent(zoom: number, oneToOneZoom: number): number {
  if (oneToOneZoom > 1.001) return Math.round((zoom / oneToOneZoom) * 100)
  return Math.round(zoom * 100)
}

export function wheelZoomFactor(deltaY: number): number {
  if (!Number.isFinite(deltaY) || deltaY === 0) return 1
  return Math.exp(-deltaY * 0.002)
}

export function cameraForPreset(
  preset: SketchZoomPreset,
  natural: { width: number; height: number } | null | undefined,
  display: { width: number; height: number },
): SketchCamera {
  if (preset === "fit") return fitCamera()
  const percent = preset === "100" ? 100 : 200
  return centeredCamera(zoomForSourcePercent(natural, display, percent), display.width, display.height)
}
